// Strip Quill-specific markup down to basic semantic HTML.
// Keeps: p, br, h1-h6, ul, ol, li, blockquote, strong, b, em, i, u, s,
//        a (href only), img (src/alt only), code, pre, hr.
// Drops: spans, classes, inline styles, data-*, ql-* attrs, font/size markup.

const ALLOWED_TAGS = new Set([
  "P", "BR", "H1", "H2", "H3", "H4", "H5", "H6",
  "UL", "OL", "LI", "BLOCKQUOTE",
  "STRONG", "B", "EM", "I", "U", "S",
  "A", "IMG", "CODE", "PRE", "HR",
]);

const ALLOWED_ATTRS: Record<string, string[]> = {
  A: ["href"],
  IMG: ["src", "alt"],
};

function clean(node: Element) {
  // Remove all attributes except a small allowlist per tag.
  const keep = ALLOWED_ATTRS[node.tagName] ?? [];
  for (const attr of Array.from(node.attributes)) {
    if (!keep.includes(attr.name.toLowerCase())) {
      node.removeAttribute(attr.name);
    }
  }
  // Sanitize href to safe schemes.
  if (node.tagName === "A") {
    const href = node.getAttribute("href") || "";
    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) node.removeAttribute("href");
    else node.setAttribute("rel", "noopener noreferrer");
  }
}

function walk(node: Node) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      walk(el);
      if (!ALLOWED_TAGS.has(el.tagName)) {
        // Unwrap: replace element with its children.
        const parent = el.parentNode!;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      } else {
        clean(el);
      }
    } else if (child.nodeType === Node.COMMENT_NODE) {
      child.parentNode?.removeChild(child);
    }
  }
}

export function sanitizeQuillHtml(html: string): string {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    // SSR/prerender fallback: strip tags/attrs we know are unsafe via regex.
    return html
      .replace(/<\/?span[^>]*>/gi, "")
      .replace(/\s(class|style|data-[\w-]+|id)="[^"]*"/gi, "")
      .replace(/&nbsp;/g, " ");
  }
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root = doc.getElementById("root")!;
  walk(root);
  return root.innerHTML.replace(/&nbsp;/g, " ");
}
